import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'

interface ExchangeCancellationModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onConfirm: (reason: string, notes: string | null) => void
  isLoading?: boolean
}

const CANCELLATION_REASONS = [
  'changed_mind',
  'wrong_request',
  'found_better_option',
  'no_longer_needed',
  'other',
] as const

export function ExchangeCancellationModal({
  open,
  onOpenChange,
  onConfirm,
  isLoading = false,
}: ExchangeCancellationModalProps) {
  const { t } = useTranslation()
  const [selectedReason, setSelectedReason] = useState<string>('')
  const [notes, setNotes] = useState('')

  const handleConfirm = () => {
    if (!selectedReason) return
    onConfirm(selectedReason, notes.trim() || null)
    setSelectedReason('')
    setNotes('')
  }

  const handleClose = () => {
    setSelectedReason('')
    setNotes('')
    onOpenChange(false)
  }

  const showNotesInput = selectedReason === 'other'

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{t('exchanges.detail.cancellationModal.title')}</DialogTitle>
          <DialogDescription>{t('exchanges.detail.cancellationModal.description')}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>{t('exchanges.detail.cancellationModal.reasonLabel')}</Label>
            <Select value={selectedReason} onValueChange={setSelectedReason}>
              <SelectTrigger>
                <SelectValue placeholder={t('exchanges.detail.cancellationModal.reasonPlaceholder')} />
              </SelectTrigger>
              <SelectContent>
                {CANCELLATION_REASONS.map((reason) => (
                  <SelectItem key={reason} value={reason}>
                    {t(`exchanges.detail.cancellationModal.reasons.${reason}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {showNotesInput && (
            <div className="space-y-2">
              <Label>{t('exchanges.detail.cancellationModal.notesLabel')}</Label>
              <Textarea
                value={notes}
                onChange={(event) => setNotes(event.target.value)}
                placeholder={t('exchanges.detail.cancellationModal.notesPlaceholder')}
                rows={3}
              />
            </div>
          )}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={handleClose} disabled={isLoading}>
            {t('exchanges.detail.cancellationModal.cancel')}
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={handleConfirm}
            disabled={!selectedReason || isLoading}
          >
            {isLoading ? t('exchanges.detail.cancellationModal.submitting') : t('exchanges.detail.cancellationModal.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
